<template>
	<v-card elevation="0" v-if="selectedHospital !== null">
		<v-card-title class="card-header">
			<v-row no-gutters dense>
				{{ $t('view') }} {{ $t('hospital') }}
				<v-spacer />
				<v-icon @click="synchronizeDrawers" class="white-icon mr-2">mdi-pencil</v-icon>
				<v-icon @click="closeDrawer" class="white-icon">mdi-close</v-icon>
			</v-row>
		</v-card-title>
		<v-card-text class="pa-4 overflow-y-auto" style="height: calc(100vh - 64px)">
			<v-row dense class="flex-column" style="height: calc(100vh - 88px)">
				<v-col class="text-left flex-grow-0">
					<v-chip pill label color="primary2" class="font-weight-bold white--text font-size16">
						{{ selectedHospital.hospitalName }}
					</v-chip>
				</v-col>
				<v-col class="text-left flex-grow-0">
					<span>{{ selectedHospital.medicalInstitutionData?.institutionName }}</span>
				</v-col>
				<v-col class="text-left flex-grow-0">
					<span>{{ selectedHospital?.address }}</span>
				</v-col>
				<v-col class="flex-grow-0">
					<v-card elevation="3" class="pa-1 mt-1 text-left">
						<span class="table-font-size ml-1">{{ selectedHospital.description }}</span>
					</v-card>
				</v-col>
				<v-col class="font-weight-bold black--text font-size18 flex-grow-0">
					{{ tablets?.length ? `${$t('tablets')} (${tablets.length})` : $t('no-tablets-available') }}
				</v-col>
				<v-col class="flex-grow-1">
					<div ref="map" style="width: 100%; height: 100%"></div>
				</v-col>
			</v-row>
			<v-row
				v-if="getEcpMappingLocation?.length"
				dense
				class="data-table-outline d-flex my-4 pa-2 mx-3 justify-space-between">
				<div class="d-flex align-center">
					<span class="mx-2 font-bold font-size14">{{ $t('migrate-users') }}</span>
					<v-chip color="primary2" dark small v-if="getEcpMappingLocation.userCreationIsAutomatic">
						{{ $t('automatically') }}
					</v-chip>
					<v-chip color="primary" dark small v-if="!getEcpMappingLocation.userCreationIsAutomatic">
						{{ $t('manually') }}
					</v-chip>
				</div>

				<div class="d-flex align-center">
					<span class="mx-2 font-bold font-size14">{{ $t('migrate-residents') }}</span>
					<v-chip color="primary2" dark small v-if="getEcpMappingLocation.residentCreationIsAutomatic">
						{{ $t('automatically') }}
					</v-chip>
					<v-chip color="primary" dark small class="mr-2" v-if="!getEcpMappingLocation.residentCreationIsAutomatic">
						{{ $t('manually') }}
					</v-chip>
				</div>
			</v-row>
		</v-card-text>
	</v-card>
</template>

<script>
import { mapGetters, mapActions, mapState } from 'vuex';
import { mailPurple, phonePurple, hospitalBlack } from '@/assets';
import { formatterFilter } from '@/utils/luxon-formater';
const DEFAULT_API_KEY = 'AIzaSyAhCWThHWyjbhS2s2qixFZLTdbkI4HqUGw';
const DEFAULT_MAP_ID = 'DEMO_MAP_ID';

export default {
	name: 'HospitalViewDrawer',
	components: {},
	data: () => ({
		apiKey: DEFAULT_API_KEY,
		mapId: DEFAULT_MAP_ID,
		initialCenter: { lat: 43.7, lng: -88.5 },
		initialZoom: 6,

		map: null,
		infoWindow: null,
		markers: [], // AdvancedMarkerElement[]
		clusterer: null, // MarkerClusterer
		clustererReady: false,

		manual: true,
		automatic: false,
		mailPurple: mailPurple,
		phonePurple: phonePurple,
		hospitalBlack: hospitalBlack,
		relatedHospitals: [],
		showHospitals: false,
		tablets: null,
	}),
	async mounted () {
		this.tablets = this.selectedHospital.tabletsInfo.map((tab) => {
			return {
				...tab,
				tabletAdditionalData: JSON.parse(tab.tabletAdditionalData),
			};
		});
		this.ensureScripts().then(() => {
			this.initMap();
			this.renderMarkers(this.tablets);
		});
	},
	beforeDestroy () {
		this.$store.commit('SET_EDIT_DETAILS_DRAWER', false);
	},
	computed: {
		...mapGetters({
			selectedHospital: 'hospitals/getSelectedHospital',
		}),
		...mapState({
			getEcpMappingLocation: (state) => state.hospitals.getEcpMappingLocation,
			allVerifiers: (state) => state.verifiers.allverifires,
		}),
		hospitalSpecializationTitle: function () {
			const fromVerifier = this.allVerifiers.filter(
				(v) =>
					v.rvDomain === 'HOSPITAL_SPECIALIZATION' && v.lowValue === this.selectedHospital.hospitalSpetializationId,
			);
			if (fromVerifier == null || fromVerifier.length === 0) {
				return '';
			} else {
				return fromVerifier[0].shortDescription;
			}
		},
	},
	methods: {
		formatterFilter,
		...mapActions({
			getHospitalById: 'hospitals/getHospitalById',
		}),
		closeDrawer () {
			this.$store.commit('hospitals/SELECTED_HOSPITAL');
			this.$store.commit('SET_EDIT_DETAILS_DRAWER', false);
		},
		synchronizeDrawers () {
			// open drawer for edit
			this.$store.commit('SET_UPDATE_DETAILS_DRAWER', {
				updateDrawer: true,
				page2: 'offices',
			});
		},
		async ensureScripts () {
			if (!(window.google && window.google.maps)) {
				await this.loadScript(
					`https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(this.apiKey)}&v=weekly&libraries=marker`,
					{ async: true, defer: true },
				);
			}
			if (!window.markerClusterer) {
				await this.loadScript('https://unpkg.com/@googlemaps/markerclusterer/dist/index.min.js');
			}
			this.clustererReady = !!window.markerClusterer;
		},
		loadScript (src, opts = {}) {
			return new Promise((resolve, reject) => {
				const existing = document.querySelector(`script[src="${src}"]`);
				if (existing) {
					if (existing.dataset.loaded === 'true') return resolve();
					existing.addEventListener('load', resolve);
					existing.addEventListener('error', reject);
					return;
				}
				const s = document.createElement('script');
				s.src = src;
				if (opts.async) s.async = true;
				if (opts.defer) s.defer = true;
				s.onload = () => {
					s.dataset.loaded = 'true';
					resolve();
				};
				s.onerror = reject;
				document.head.appendChild(s);
			});
		},
		initMap () {
			this.map = new window.google.maps.Map(this.$refs.map, {
				mapId: this.mapId,
				center: this.initialCenter,
				zoom: this.initialZoom,
				disableDefaultUI: true,
				mapTypeId: 'hybrid',
			});
			this.infoWindow = new window.google.maps.InfoWindow({
				headerDisabled: true,
			});
		},
		renderMarkers (rows) {
			if (!this.map) return;

			this.clearMarkersAndClusterer();

			const bounds = new window.google.maps.LatLngBounds();
			let validCount = 0;

			rows.forEach((row) => {
				const meta = row.tabletAdditionalData;
				const lat = Number(meta?.latitude);
				const lng = Number(meta?.longitude);
				if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

				const color = this.colorForBattery(row.batteryLevel);

				const marker = new window.google.maps.marker.AdvancedMarkerElement({
					position: { lat, lng },
					map: this.map, // setting map is fine even with clusterer
					title: row.userName || 'Device',
					content: this.createMarkerDot(color),
				});

				marker.addListener('gmp-click', () => {
					const info = this.buildInfoContent(row, meta);
					this.infoWindow.setContent(info.html);
					this.infoWindow.open({ map: this.map, anchor: marker });
					// Attach close button event after InfoWindow is rendered
					window.setTimeout(() => {
						const btn = document.getElementById(info.closeId);
						if (btn) {
							btn.onclick = () => this.infoWindow.close();
						}
					}, 0);
					// Click-to-zoom behavior
					this.map.panTo(marker.position);
					const targetZoom = 14;
					if (this.map.getZoom() < targetZoom) this.map.setZoom(targetZoom);
				});

				this.markers.push(marker);
				bounds.extend(marker.position);
				validCount++;
			});

			// Clustering
			if (this.clustererReady && this.markers.length) {
				this.clusterer = new window.markerClusterer.MarkerClusterer({
					map: this.map,
					markers: this.markers,
					// algorithm: new window.markerClusterer.GridAlgorithm({ maxZoom: 16, radius: 60 }),
				});
			}

			if (!bounds.isEmpty() && validCount > 0) {
				this.map.fitBounds(bounds, { top: 64, right: 16, bottom: 16, left: 16 });

				const listener = this.map.addListener('idle', () => {
					const currentZoom = this.map.getZoom();
					this.map.setZoom(currentZoom - 2);
					window.google.maps.event.removeListener(listener);
				});
			}
		},
		clearMarkersAndClusterer () {
			if (this.clusterer) {
				this.clusterer.setMap(null);
				this.clusterer = null;
			}
			this.markers.forEach((m) => (m.map = null));
			this.markers = [];
		},
		computeBounds (ms) {
			const bounds = new window.google.maps.LatLngBounds();
			let expanded = false;
			ms.forEach((m) => {
				if (m.position) {
					bounds.extend(m.position);
					expanded = true;
				}
			});
			return expanded ? bounds : null;
		},
		colorForBattery (pct) {
			if (pct >= 60) return '#2E7D32';
			if (pct >= 30) return '#F9A825';
			return '#C62828';
		},
		createMarkerDot (color) {
			const div = document.createElement('div');
			div.style.width = '16px';
			div.style.height = '16px';
			div.style.borderRadius = '50%';
			div.style.background = color;
			div.style.border = '2px solid white';
			div.style.boxShadow = '0 0 0 1px rgba(0,0,0,.2)';
			return div;
		},
		escapeHTML (val) {
			return String(val ?? '').replace(
				/[&<>"']/g,
				(c) =>
					({
						'&': '&amp;',
						'<': '&lt;',
						'>': '&gt;',
						'"': '&quot;',
						'\'': '&#039;',
					}[c]),
			);
		},
		buildInfoContent (row, meta) {
			const closeId = `infowindow-close-${Math.random().toString(36).substring(2, 9)}`;
			const html = [
				`<div style="font-weight:600;font-size:14px;margin-bottom:4px;display:flex;align-items:center;justify-content:space-between;text-align:left">
					<span>${this.escapeHTML(row.userName || 'Unknown user')}</span>
					<button id="${closeId}" style="margin-left:8px;background:none;border:none;cursor:pointer;font-size:28px;width:36px;height:36px;line-height:1;display:flex;align-items:center;justify-content:center;">&times;</button>
					</div>`,
				`<div style="text-align:left"><strong>Name:</strong>
					${this.escapeHTML(row.tabletAdditionalData?.deviceName || '-')}
					</div>`,
				`<div style="text-align:left"><strong>Facility:</strong> ${this.escapeHTML(row.hospitalName || '-')}
					(ID ${row.hospitalId})
					</div>`,
				`<div style="text-align:left">
					<strong>OS:</strong> ${this.escapeHTML(row.os || '-')}&nbsp;
					<strong>Battery:</strong> ${row.batteryLevel}%
					</div>`,
				`<div style="text-align:left"><strong>App:</strong> ${this.escapeHTML(row.appVersion || '-')}</div>`,
			];

			if (row.tabletAdditionalData?.anyDeskId) {
				html.push(
					`<div style="text-align:left"><strong>AnyDesk:</strong> 
						${this.escapeHTML(row.tabletAdditionalData.anyDeskId)}
					</div>`,
				);
			}

			if (row.tabletAdditionalData?.serialNumber) {
				html.push(
					`<div style="text-align:left"><strong>Serial Number:</strong> ${this.escapeHTML(
						row.tabletAdditionalData.serialNumber,
					)}</div>`,
				);
			}

			html.push(
				`<div style="text-align:left"><strong>Model:</strong>
					${this.escapeHTML(meta?.deviceModel || '-')}
					&nbsp; <strong>Maker:</strong> ${this.escapeHTML(meta?.deviceManufacturer || '-')}</div>`,
				`<div style="text-align:left"><strong>Build:</strong> ${this.escapeHTML(meta?.buildNumber || '-')}</div>`,
				`<div style="text-align:left"><strong>Sec. Patch:</strong>
					${this.escapeHTML(meta?.securityPatch || '-')}
					&nbsp; <strong>SDK:</strong> ${this.escapeHTML(String(meta?.sdkInt ?? '-'))}</div>`,
				`<div style="text-align:left"><strong>Network:</strong>
					${this.escapeHTML(meta?.networkType || '-')} (${this.escapeHTML(meta?.ipAddress || '-')})
					</div>`,
				`<div style="text-align:left"><strong>Reported:</strong>
					${row.reportTimestamp ? this.escapeHTML(this.formatterFilter(row.reportTimestamp, 'datetime_med')) : '-'}
					</div>`,
			);

			return { html: `<div style="max-width:300px">${html.join('')}</div>`, closeId };
		},
	},
	watch: {
		selectedHospital: {
			handler (val) {
				this.tablets = val.tabletsInfo.map((tab) => {
					return {
						...tab,
						tabletAdditionalData: JSON.parse(tab.tabletAdditionalData),
					};
				});
				this.ensureScripts().then(() => {
					this.initMap();
					this.renderMarkers(this.tablets);
				});
			},
			deep: true,
		},
	},
};
</script>
